/**
 * Alkisen UI primitives. Each component is a thin React wrapper around
 * the .ui-* class primitives in src/styles/globals.css.
 *
 * Keep this file flat so the whole inventory is discoverable in one place.
 */
import {
  type ReactNode, type ButtonHTMLAttributes, type InputHTMLAttributes,
  type TextareaHTMLAttributes, type SelectHTMLAttributes, type CSSProperties,
  type ReactElement, type ElementType,
  forwardRef, useEffect, useRef, useState, useCallback,
} from 'react';
import { clsx } from 'clsx';
import { Icon, type IconName } from '../icons';

// ─── Button ───────────────────────────────────────────────────────────
export type ButtonVariant = 'primary' | 'ghost' | 'ruby' | 'icon';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  iconLeft?: IconName;
  iconRight?: IconName;
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', iconLeft, iconRight, loading, fullWidth,
     className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        'ui-btn',
        variant === 'primary' && 'ui-btn--primary',
        variant === 'ghost' && 'ui-btn--ghost',
        variant === 'ruby' && 'ui-btn--ruby',
        variant === 'icon' && 'ui-btn--icon',
        size === 'sm' && 'ui-btn--sm',
        size === 'lg' && 'ui-btn--lg',
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Icon name="spinner" className="animate-spin" />
      ) : (
        iconLeft && <Icon name={iconLeft} size={size === 'sm' ? 14 : 16} />
      )}
      {children}
      {!loading && iconRight && <Icon name={iconRight} size={size === 'sm' ? 14 : 16} />}
    </button>
  ),
);
Button.displayName = 'Button';

// ─── IconButton ───────────────────────────────────────────────────────
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  label: string; // a11y: required
  size?: 'sm' | 'md' | 'lg';
  variant?: 'icon' | 'primary' | 'ghost';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, size = 'md', variant = 'icon', className, ...rest }, ref) => (
    <button
      ref={ref}
      aria-label={label}
      title={label}
      className={clsx(
        'ui-btn',
        variant === 'icon' && 'ui-btn--icon',
        variant === 'primary' && 'ui-btn--primary',
        variant === 'ghost' && 'ui-btn--ghost',
        (variant === 'primary' || variant === 'ghost') && 'aspect-square !px-0',
        size === 'sm' && 'h-8 w-8',
        size === 'md' && 'h-10 w-10',
        size === 'lg' && 'h-12 w-12',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 14 : size === 'lg' ? 22 : 18} />
    </button>
  ),
);
IconButton.displayName = 'IconButton';

// ─── Card ─────────────────────────────────────────────────────────────
export interface CardProps {
  children: ReactNode;
  interactive?: boolean;
  glow?: boolean;
  className?: string;
  onClick?: () => void;
  as?: 'div' | 'article' | 'section' | 'a';
  href?: string;
}

export function Card({ children, interactive, glow, className, onClick, as = 'div', href }: CardProps) {
  const Tag: ElementType = href ? 'a' : as;
  const extraProps = href ? { href } : {};
  return (
    <Tag
      className={clsx('ui-card p-4', interactive && 'ui-card--interactive', glow && 'ui-card--glow', className)}
      onClick={onClick}
      {...extraProps}
    >
      {children}
    </Tag>
  );
}

// ─── Input ────────────────────────────────────────────────────────────
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  iconLeft?: IconName;
  label?: string;
  hint?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ iconLeft, label, hint, error, className, id, ...rest }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).slice(2, 8)}`;
    return (
      <div className="grid gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-xs font-medium uppercase tracking-wider text-mist">
            {label}
          </label>
        )}
        <div className="relative">
          {iconLeft && (
            <span className="pointer-events-none absolute inset-y-0 left-3 grid place-items-center text-mist">
              <Icon name={iconLeft} size={16} />
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={clsx('ui-input', iconLeft && 'pl-10', error && 'border-danger', className)}
            aria-invalid={Boolean(error)}
            {...rest}
          />
        </div>
        {error ? (
          <p className="text-xs text-[color:var(--ui-color-danger)]">{error}</p>
        ) : hint ? (
          <p className="text-xs text-mist">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

// ─── Textarea ─────────────────────────────────────────────────────────
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, className, id, ...rest }, ref) => {
    const inputId = id || `txta-${Math.random().toString(36).slice(2, 8)}`;
    return (
      <div className="grid gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-xs font-medium uppercase tracking-wider text-mist">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          className={clsx('ui-input min-h-[6rem] resize-y', error && 'border-danger', className)}
          aria-invalid={Boolean(error)}
          {...rest}
        />
        {error ? (
          <p className="text-xs text-[color:var(--ui-color-danger)]">{error}</p>
        ) : hint ? (
          <p className="text-xs text-mist">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

// ─── Pill / Chip ──────────────────────────────────────────────────────
export interface PillProps {
  children: ReactNode;
  active?: boolean;
  variant?: 'default' | 'ruby' | 'gold';
  icon?: IconName;
  onClick?: () => void;
  className?: string;
}

export function Pill({ children, active, variant = 'default', icon, onClick, className }: PillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'ui-pill',
        active && 'ui-pill--active',
        variant === 'ruby' && 'ui-pill--ruby',
        variant === 'gold' && 'ui-pill--gold',
        onClick && 'cursor-pointer',
        !onClick && 'cursor-default',
        className,
      )}
    >
      {icon && <Icon name={icon} size={12} />}
      {children}
    </button>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────
export interface BadgeProps {
  children: ReactNode;
  tone?: 'gold' | 'ruby' | 'mist' | 'success' | 'danger';
  className?: string;
}

export function Badge({ children, tone = 'mist', className }: BadgeProps) {
  return (
    <span className={clsx('ui-badge', `ui-badge--${tone}`, className)}>{children}</span>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────
export interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: 'sm' | 'md' | 'lg' | 'pill';
}

export function Skeleton({ className, width, height = 16, rounded = 'sm' }: SkeletonProps) {
  return (
    <div
      className={clsx(
        'ui-skeleton',
        rounded === 'sm' && 'rounded',
        rounded === 'md' && 'rounded-lg',
        rounded === 'lg' && 'rounded-xl',
        rounded === 'pill' && 'rounded-full',
        className,
      )}
      style={{ width, height }}
    />
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────
export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return <Icon name="spinner" size={size} className={clsx('animate-spin', className)} />;
}

// ─── EmptyState ───────────────────────────────────────────────────────
export interface EmptyStateProps {
  icon?: IconName;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon = 'bottle-empty', title, description, action, className }: EmptyStateProps) {
  return (
    <div className={clsx('ui-empty', className)}>
      <div className="grid h-14 w-14 place-items-center rounded-full bg-ink-soft text-mist">
        <Icon name={icon} size={26} />
      </div>
      <h3 className="font-serif text-2xl text-parchment">{title}</h3>
      {description && <p className="max-w-sm text-sm text-mist">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center p-4 backdrop-blur-md"
      style={{ background: 'var(--ui-modal-scrim)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        className={clsx(
          'ui-card animate-rise flex w-full max-h-[90vh] flex-col p-6',
          size === 'sm' && 'max-w-sm',
          size === 'md' && 'max-w-lg',
          size === 'lg' && 'max-w-3xl',
        )}
      >
        {title && (
          <div className="mb-4 flex shrink-0 items-center justify-between">
            <h3 className="ui-section-title">{title}</h3>
            <IconButton icon="x" label="Lukk" size="sm" onClick={onClose} />
          </div>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && <div className="mt-6 flex shrink-0 justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

// ─── Drawer (right-side) ──────────────────────────────────────────────
export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  side?: 'left' | 'right';
  /** Max width in CSS units (px/rem). On phones the drawer caps at 100vw. */
  width?: string;
  /** Sticky bottom action area — stays visible while body scrolls. */
  footer?: ReactNode;
}

export function Drawer({
  open,
  onClose,
  title,
  children,
  side = 'right',
  width = '420px',
  footer,
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={clsx(
          'fixed inset-0 z-40 transition-opacity duration-300',
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        style={{ background: 'var(--ui-modal-scrim)' }}
        onClick={onClose}
      />
      <aside
        className={clsx(
          'fixed top-0 z-50 flex h-full flex-col border-line bg-ink-soft transition-transform duration-300',
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
        )}
        style={{
          width: `min(${width}, 100vw)`,
          transform: open
            ? 'translateX(0)'
            : side === 'right'
              ? 'translateX(100%)'
              : 'translateX(-100%)',
        }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-line bg-ink-soft/80 p-4 backdrop-blur">
          <h3 className="ui-section-title">{title}</h3>
          <IconButton icon="x" label="Lukk" size="sm" onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto p-4 pb-24">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-line bg-ink-soft/95 p-4 backdrop-blur">{footer}</div>
        )}
      </aside>
    </>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────
export interface TabsProps<T extends string = string> {
  tabs: { id: T; label: string; icon?: IconName }[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
}

export function Tabs<T extends string>({ tabs, active, onChange, className }: TabsProps<T>) {
  return (
    <div className={clsx('inline-flex gap-1 rounded-lg border border-line bg-ink-soft p-1', className)}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={clsx(
            'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all',
            active === tab.id
              ? 'bg-candle text-on-primary shadow-glow'
              : 'text-mist hover:bg-overlay-soft hover:text-parchment',
          )}
        >
          {tab.icon && <Icon name={tab.icon} size={14} />}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── DropZone ─────────────────────────────────────────────────────────
export interface DropZoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  children?: ReactNode;
  className?: string;
}

export function DropZone({ onFiles, accept = 'image/*', multiple = false, children, className }: DropZoneProps) {
  const [active, setActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback((list: FileList | null) => {
    if (!list || list.length === 0) return;
    onFiles(Array.from(list));
  }, [onFiles]);

  return (
    <div
      className={clsx('ui-dropzone', active && 'ui-dropzone--active', className)}
      onDragOver={(e) => { e.preventDefault(); setActive(true); }}
      onDragLeave={() => setActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setActive(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {children ?? (
        <div className="grid gap-2 text-center">
          <Icon name="upload" size={28} className="mx-auto text-candle" />
          <p className="text-sm text-parchment">Slipp bildet her, eller klikk for å velge</p>
          <p className="text-xs text-mist">PNG, JPG opp til 10 MB</p>
        </div>
      )}
    </div>
  );
}

// ─── Toast (lightweight in-page) ──────────────────────────────────────
export type ToastTone = 'success' | 'danger' | 'info' | 'warning';
export interface ToastMsg {
  id: string;
  title: string;
  description?: string;
  tone?: ToastTone;
}

export function Toast({ msg, onClose }: { msg: ToastMsg; onClose: () => void }): ReactElement {
  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [onClose]);

  const iconByTone = {
    success: 'check-circle',
    danger: 'x-circle',
    info: 'info',
    warning: 'warning',
  } as const;
  const tone = msg.tone ?? 'info';

  return (
    <div className="ui-card animate-rise flex items-start gap-3 p-3" role="status">
      <Icon name={iconByTone[tone]} size={18} className={clsx(
        tone === 'success' && 'text-[color:var(--ui-color-success)]',
        tone === 'danger' && 'text-[color:var(--ui-color-danger)]',
        tone === 'warning' && 'text-[color:var(--ui-color-warning)]',
        tone === 'info' && 'text-[color:var(--ui-color-info)]',
      )} />
      <div className="grid gap-0.5">
        <p className="text-sm font-semibold text-parchment">{msg.title}</p>
        {msg.description && <p className="text-xs text-mist">{msg.description}</p>}
      </div>
      <IconButton icon="x" label="Lukk varsel" size="sm" onClick={onClose} className="ml-auto" />
    </div>
  );
}

// ─── Switch (toggle) ────────────────────────────────────────────────────
// Generic on/off control. Used by settings panels — Vinliste today,
// pricing / stock-count / locations next — so keep it data-shape-agnostic.
export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Accessible name. Visible label text should come from SwitchRow instead. */
  label: string;
  disabled?: boolean;
  className?: string;
}

export function Switch({ checked, onChange, label, disabled, className }: SwitchProps): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx('ui-switch', className)}
    >
      <span className="ui-switch__thumb" />
    </button>
  );
}

export interface SwitchRowProps extends Omit<SwitchProps, 'className'> {
  description?: string;
  className?: string;
}

export function SwitchRow({ label, description, checked, onChange, disabled, className }: SwitchRowProps): ReactElement {
  return (
    <div className={clsx('ui-switch-row', className)}>
      <span className="grid gap-0.5">
        <span>{label}</span>
        {description && <span className="text-xs text-mist">{description}</span>}
      </span>
      <Switch checked={checked} onChange={onChange} label={label} disabled={disabled} />
    </div>
  );
}

// ─── Radio group ────────────────────────────────────────────────────────
export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  disabled?: boolean;
  className?: string;
}

export function RadioGroup({ name, value, onChange, options, disabled, className }: RadioGroupProps): ReactElement {
  return (
    <div className={clsx('ui-radio-group', className)}>
      {options.map((opt) => {
        const optDisabled = disabled || opt.disabled;
        return (
          <label key={opt.value} className={clsx('ui-radio-option', optDisabled && 'ui-radio-option--disabled')}>
            <input
              type="radio"
              name={name}
              checked={value === opt.value}
              disabled={optDisabled}
              onChange={() => onChange(opt.value)}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

// ─── Select ─────────────────────────────────────────────────────────────
export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  hint?: string;
  options: SelectOption[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, hint, options, className, id, ...rest }, ref) => {
    const selectId = id || `select-${Math.random().toString(36).slice(2, 8)}`;
    return (
      <div className="grid gap-1.5">
        {label && (
          <label htmlFor={selectId} className="text-xs font-medium uppercase tracking-wider text-mist">
            {label}
          </label>
        )}
        <select ref={ref} id={selectId} className={clsx('ui-input', className)} {...rest}>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {hint && <p className="text-xs text-mist">{hint}</p>}
      </div>
    );
  },
);
Select.displayName = 'Select';

// ─── Chip (colored label tag) ────────────────────────────────────────────
// Distinct from Pill: Pill is for filter/selection controls with an
// active state; Chip is a plain, arbitrarily-colored label (e.g. a
// category tag whose color is data-driven).
export interface ChipProps {
  children: ReactNode;
  bg: string;
  fg: string;
  muted?: boolean;
  className?: string;
}

export function Chip({ children, bg, fg, muted, className }: ChipProps): ReactElement {
  return (
    <span className={clsx('ui-chip', muted && 'ui-chip--muted', className)} style={{ backgroundColor: bg, color: fg }}>
      {children}
    </span>
  );
}

// ─── Sortable row (generic drag-handle list row) ─────────────────────────
// Presentational only — wire it up with @dnd-kit's `useSortable()` (or any
// other drag library) at the call site: pass its `transform`/`transition`
// into `style` and its `attributes`/`listeners` onto `<DragHandle>`.
export const DragHandle = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label?: string }>(
  ({ label = 'Dra for å endre rekkefølge', className, ...rest }, ref) => (
    <button ref={ref} type="button" aria-label={label} className={clsx('ui-drag-handle', className)} {...rest}>
      <Icon name="drag" size={14} />
    </button>
  ),
);
DragHandle.displayName = 'DragHandle';

export interface SortableRowProps {
  /** CSS grid-template-columns for this row and its matching TableHead. */
  gridTemplateColumns: string;
  isDragging?: boolean;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}

export const SortableRow = forwardRef<HTMLDivElement, SortableRowProps>(
  ({ gridTemplateColumns, isDragging, style, className, children }, ref) => (
    <div
      ref={ref}
      style={{ gridTemplateColumns, ...style }}
      className={clsx('ui-sortable-row', isDragging && 'ui-sortable-row--dragging', className)}
    >
      {children}
    </div>
  ),
);
SortableRow.displayName = 'SortableRow';

export interface TableHeadProps {
  gridTemplateColumns: string;
  columns: ReactNode[];
  className?: string;
}

export function TableHead({ gridTemplateColumns, columns, className }: TableHeadProps): ReactElement {
  return (
    <div style={{ gridTemplateColumns }} className={clsx('ui-table-head', className)}>
      {columns.map((col, i) => <span key={i}>{col}</span>)}
    </div>
  );
}

// ─── Section card (Card + small uppercase title, optional action) ───────
export interface SectionCardProps {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function SectionCard({ title, description, action, children, className }: SectionCardProps): ReactElement {
  return (
    <Card className={clsx('grid gap-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="ui-card-title">{title}</h2>
        {action}
      </div>
      {description && <p className="text-xs text-mist">{description}</p>}
      {children}
    </Card>
  );
}

export { Icon } from '../icons';
export type { IconName } from '../icons';
